import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Plus, Pencil, Trash2, LogOut, Car, Image, DollarSign, Eye, EyeOff, X, Upload, FileText } from 'lucide-react';
import { carPath } from '../lib/carRoutes';

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'rtr_admin_token';

const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCar, setEditCar] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);

  const fetchCars = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/cars`, { headers: authHeaders() });
      setCars(data.data);
    } catch (err) { console.error('Failed to fetch cars:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCars(); }, [fetchCars]);

  const deleteCar = async (id) => {
    if (!window.confirm('Delete this car listing?')) return;
    try {
      await axios.delete(`${API}/api/cars/${id}`, { headers: authHeaders() });
      setCars(prev => prev.filter(c => c.id !== id));
    } catch (err) { alert('Failed to delete'); }
  };

  const handleLogout = async () => { await logout(); navigate(carPath('/cars/login')); };

  const getImageUrl = (car) => {
    if (car.thumbnail) {
      const path = car.thumbnail.replace('rtr-cars/cars/', '');
      return `${API}/api/cars/images/${path}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="bg-gray-900 border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">Admin Dashboard</h1>
            <p className="text-xs text-gray-500">{user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/blog/admin')} className="text-gray-400 hover:text-white text-sm flex items-center gap-1" data-testid="blog-admin-btn">
              <FileText size={16} /> Blog
            </button>
            <button onClick={() => navigate('/downloads')} className="text-gray-400 hover:text-white text-sm flex items-center gap-1" data-testid="files-link-btn">
              <FileText size={16} /> Files
            </button>
            <button onClick={() => navigate(carPath('/cars'))} className="text-gray-400 hover:text-white text-sm flex items-center gap-1" data-testid="view-showroom-btn">
              <Eye size={16} /> View Showroom
            </button>
            <button onClick={handleLogout} className="text-gray-400 hover:text-red-400 text-sm flex items-center gap-1" data-testid="logout-btn">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Car Listings ({cars.length})</h2>
          <button
            onClick={() => { setEditCar(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            data-testid="add-car-btn"
          >
            <Plus size={16} /> Add Car
          </button>
        </div>

        {loading ? (
          <div className="text-center py-20 text-gray-500">Loading...</div>
        ) : cars.length === 0 ? (
          <div className="text-center py-20">
            <Car size={48} className="mx-auto text-gray-700 mb-4" />
            <p className="text-gray-500">No car listings yet</p>
            <button onClick={() => setShowForm(true)} className="text-orange-500 hover:underline mt-2 text-sm">Add your first car</button>
          </div>
        ) : (
          <div className="space-y-3">
            {cars.map(car => (
              <div key={car.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center gap-4" data-testid={`admin-car-${car.id}`}>
                <div className="w-24 h-16 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                  {getImageUrl(car) ? (
                    <img src={getImageUrl(car)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Car size={20} className="text-gray-700" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-sm truncate">{car.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                    <span>{car.year}</span>
                    <span>{car.mileage}</span>
                    <span className="flex items-center gap-1"><Image size={12} />{(car.images || []).length}</span>
                    {car.show_price && car.price ? (
                      <span className="text-orange-500 flex items-center gap-1"><DollarSign size={12} />{car.price.toLocaleString()}</span>
                    ) : (
                      <span className="italic">Inquire for Price</span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs ${car.status === 'available' ? 'bg-green-500/10 text-green-400' : 'bg-red-500/10 text-red-400'}`}>
                      {car.status}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={() => setUploadingFor(car)} className="flex items-center gap-1.5 bg-blue-600/20 hover:bg-blue-600/40 text-blue-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors" data-testid={`upload-imgs-${car.id}`}>
                    <Upload size={14} /> Upload Photos
                  </button>
                  <button onClick={() => { setEditCar(car); setShowForm(true); }} className="flex items-center gap-1.5 bg-orange-600/20 hover:bg-orange-600/40 text-orange-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors" data-testid={`edit-car-${car.id}`}>
                    <Pencil size={14} /> Edit
                  </button>
                  <button onClick={() => deleteCar(car.id)} className="flex items-center gap-1.5 bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors" data-testid={`delete-car-${car.id}`}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && <CarForm car={editCar} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); fetchCars(); }} />}
      {uploadingFor && <ImageUploader car={uploadingFor} onClose={() => { setUploadingFor(null); fetchCars(); }} />}
    </div>
  );
};


const CarForm = ({ car, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: car?.title || '', year: car?.year || new Date().getFullYear(), make: car?.make || '', model: car?.model || '',
    mileage: car?.mileage || '', engine: car?.engine || '', power: car?.power || '',
    price: car?.price || '', show_price: car?.show_price || false, description: car?.description || '', status: car?.status || 'available'
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, price: form.price ? parseFloat(form.price) : null, year: parseInt(form.year) };
      if (car) {
        await axios.put(`${API}/api/cars/${car.id}`, payload, { headers: authHeaders() });
      } else {
        await axios.post(`${API}/api/cars`, payload, { headers: authHeaders() });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto pt-10 pb-10 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-lg p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white"><X size={20} /></button>
        <h2 className="text-lg font-bold mb-4">{car ? 'Edit Car' : 'Add New Car'}</h2>
        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-3 py-2 rounded-lg mb-4">{error}</div>}
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input label="Title" value={form.title} onChange={v => set('title', v)} placeholder="2024 Audi Q4 e-tron 40" required testId="form-title" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Make" value={form.make} onChange={v => set('make', v)} placeholder="Audi" required testId="form-make" />
            <Input label="Model" value={form.model} onChange={v => set('model', v)} placeholder="Q4 e-tron 40" required testId="form-model" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Year" type="number" value={form.year} onChange={v => set('year', v)} required testId="form-year" />
            <Input label="Mileage" value={form.mileage} onChange={v => set('mileage', v)} placeholder="32,248 km" required testId="form-mileage" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Engine" value={form.engine} onChange={v => set('engine', v)} placeholder="150 kW" required testId="form-engine" />
            <Input label="Power / Battery" value={form.power} onChange={v => set('power', v)} placeholder="~80 kWh" required testId="form-power" />
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <Input label="Price (USD)" type="number" value={form.price} onChange={v => set('price', v)} placeholder="Leave empty for inquire" testId="form-price" />
            <div className="flex items-center gap-2 pb-1">
              <button type="button" onClick={() => set('show_price', !form.show_price)}
                className={`w-10 h-5 rounded-full transition-colors ${form.show_price ? 'bg-orange-600' : 'bg-gray-700'} relative`}
                data-testid="form-show-price-toggle"
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${form.show_price ? 'left-5' : 'left-0.5'}`}></span>
              </button>
              <span className="text-xs text-gray-400 flex items-center gap-1">
                {form.show_price ? <Eye size={12} /> : <EyeOff size={12} />}
                {form.show_price ? 'Show price' : 'Inquire only'}
              </span>
            </div>
          </div>
          <div>
            <label className="text-gray-400 text-xs font-medium mb-1 block">Status</label>
            <select value={form.status} onChange={e => set('status', e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-orange-500"
              data-testid="form-status"
            >
              <option value="available">Available</option>
              <option value="sold">Sold</option>
              <option value="reserved">Reserved</option>
            </select>
          </div>
          <div>
            <label className="text-gray-400 text-xs font-medium mb-1 block">Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 resize-none"
              placeholder="Optional description..."
              data-testid="form-description"
            />
          </div>
          <button type="submit" disabled={saving}
            className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-lg transition-colors text-sm"
            data-testid="form-submit-btn"
          >
            {saving ? 'Saving...' : (car ? 'Update Car' : 'Add Car')}
          </button>
        </form>
      </div>
    </div>
  );
};


const ImageUploader = ({ car, onClose }) => {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(car.images || []);
  const [progress, setProgress] = useState('');

  const getImageUrl = (storagePath) => {
    const path = storagePath.replace('rtr-cars/cars/', '');
    return `${API}/api/cars/images/${path}`;
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setProgress(`Uploading ${files.length} image(s)...`);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const { data } = await axios.post(`${API}/api/cars/${car.id}/images`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setImages(data.data.images || []);
      setProgress(`${data.uploaded_count} image(s) uploaded`);
    } catch (err) {
      setProgress('Upload failed');
    } finally { setUploading(false); }
  };

  const deleteImage = async (index) => {
    try {
      const { data } = await axios.delete(`${API}/api/cars/${car.id}/images/${index}`, { headers: authHeaders() });
      setImages(data.data.images || []);
    } catch (err) { alert('Failed to delete image'); }
  };

  const setThumbnail = async (index) => {
    try {
      await axios.put(`${API}/api/cars/${car.id}/thumbnail/${index}`, {}, { headers: authHeaders() });
      setProgress('Thumbnail updated');
    } catch (err) { alert('Failed to set thumbnail'); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-y-auto pt-10 pb-10 px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-2xl p-6 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white"><X size={20} /></button>
        <h2 className="text-lg font-bold mb-1">Images — {car.title}</h2>
        <p className="text-sm text-gray-500 mb-4">{images.length} image(s)</p>

        <label className={`block w-full border-2 border-dashed border-gray-700 hover:border-orange-500 rounded-xl p-6 text-center cursor-pointer transition-colors mb-4 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload size={24} className="mx-auto text-gray-500 mb-2" />
          <span className="text-sm text-gray-400">{uploading ? progress : 'Click to upload images (max 10MB each)'}</span>
          <input type="file" multiple accept="image/*" onChange={handleUpload} className="hidden" data-testid="image-upload-input" />
        </label>

        {progress && !uploading && <p className="text-xs text-green-400 mb-3">{progress}</p>}

        {images.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {images.map((img, i) => (
              <div key={img.storage_path} className="relative group rounded-lg overflow-hidden aspect-square bg-gray-800">
                <img src={getImageUrl(img.storage_path)} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button onClick={() => setThumbnail(i)} className="p-1.5 bg-blue-600 rounded text-white text-xs" title="Set as thumbnail">
                    <Image size={14} />
                  </button>
                  <button onClick={() => deleteImage(i)} className="p-1.5 bg-red-600 rounded text-white text-xs" title="Delete">
                    <Trash2 size={14} />
                  </button>
                </div>
                {car.thumbnail === img.storage_path && (
                  <span className="absolute top-1 left-1 bg-orange-600 text-white text-[10px] px-1.5 py-0.5 rounded">Thumb</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const Input = ({ label, testId, ...props }) => (
  <div>
    <label className="text-gray-400 text-xs font-medium mb-1 block">{label}</label>
    <input
      {...props}
      onChange={e => props.onChange(e.target.value)}
      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-orange-500"
      data-testid={testId}
    />
  </div>
);

export default AdminDashboard;
